import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient, useQuery } from '@apollo/client'
import toast from 'react-hot-toast'
import { GET_ORDERS } from '../graphql/queries'
import { Order, OrderStatus, OrderLine } from '../types/order'
import { OrderService } from '../lib/orderService'
import { isOwnDelivery } from '../lib/delivery'
import { apiTime, formatTime, phoneWithAccessCode } from '../lib/format'
import SoldOutButton from '../components/SoldOutButton'
import { CheckIcon, ArrowUturnLeftIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'

const REFRESH_INTERVAL_MS = 3000 // 3 seconds - faster polling for kitchen screen as per docs
// A poll response snapshotted before a status mutation (in-flight request, server read
// staleness) overwrites the cache with the old status, snapping the card back to its old
// lane. Overrides shield the UI from any such stale write for their full TTL — they are NOT
// pruned when the server first confirms the new status, because a stale response can still
// land after that confirmation. While the server agrees with the override it is a no-op.
// Accepted trade-off: a status change made from another screen on a locally-advanced order
// is masked for at most the TTL.
const STATUS_OVERRIDE_TTL_MS = 15000

interface StatusOverride {
  status: OrderStatus
  since: string | null
  at: number
}

const KitchenDisplayPage: React.FC = () => {
  const apolloClient = useApolloClient()
  const orderService = useMemo(() => new OrderService(apolloClient), [apolloClient])
  const [completedOrderLines, setCompletedOrderLines] = useState<Set<string>>(new Set())
  // Per-order lock: the ref is the synchronous guard (React 18 batches state updates, so a
  // state-only guard wouldn't see a same-render double-tap); the state mirror drives the UI.
  // Locking per order — not globally — keeps every other order's buttons responsive while
  // one update is in flight.
  const processingRef = useRef<Set<number>>(new Set())
  const [processingOrderIds, setProcessingOrderIds] = useState<Set<number>>(new Set())
  const [statusOverrides, setStatusOverrides] = useState<Record<number, StatusOverride>>({})
  const [showAllReady, setShowAllReady] = useState(false)
  const [expandedCompletedOrders, setExpandedCompletedOrders] = useState<Set<number>>(new Set())

  const toggleOrderExpansion = (orderId: number) => {
    setExpandedCompletedOrders(prev => {
      const newSet = new Set(prev)
      if (newSet.has(orderId)) newSet.delete(orderId)
      else newSet.add(orderId)
      return newSet
    })
  }

  const todayStart = new Date()
  todayStart.setDate(todayStart.getDate() - 1)
  todayStart.setHours(0, 0, 0, 0)
  const todayEnd = new Date()
  todayEnd.setHours(23, 59, 59, 999)

  // Window on scheduled_for, NOT created_at (API team, 2026-07-10): for ASAP
  // orders the two are identical to the second (API invariant), but a released
  // pre-order keeps its old created_at — a date_from/date_to window here would
  // hide it from the kitchen forever. Rule: placement questions → date_from/
  // date_to; operational/service views (this board) → scheduled_from/_to.
  const { data, loading, refetch } = useQuery(GET_ORDERS, {
    variables: {
      filters: {
        statuses: ['CONFIRMED', 'PREPARING', 'READY', 'IN_DELIVERY', 'COMPLETED'],
        scheduled_from: todayStart.toISOString(),
        scheduled_to: todayEnd.toISOString(),
      }
    },
    fetchPolicy: 'network-only',
    pollInterval: REFRESH_INTERVAL_MS,
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    const onFocus = () => refetch()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refetch])

  // Drop overrides on TTL expiry only — never on server confirmation, since a stale
  // response can still land after the first confirming one (see STATUS_OVERRIDE_TTL_MS).
  // Runs on every data emission, plus a timer for the earliest expiry so overrides are
  // still pruned if polling stalls.
  useEffect(() => {
    const prune = () =>
      setStatusOverrides(prev => {
        const now = Date.now()
        const entries = Object.entries(prev).filter(([, o]) => now - o.at < STATUS_OVERRIDE_TTL_MS)
        return entries.length === Object.keys(prev).length ? prev : Object.fromEntries(entries)
      })
    prune()
    const expiries = Object.values(statusOverrides).map(o => o.at + STATUS_OVERRIDE_TTL_MS - Date.now())
    if (expiries.length === 0) return
    const timer = setTimeout(prune, Math.max(Math.min(...expiries), 0) + 50)
    return () => clearTimeout(timer)
  }, [data, statusOverrides])

  const orders: Order[] = useMemo(() => {
    const rawOrders = (data?.orders || []) as Order[]
    return rawOrders
      .map(o => {
        const override = statusOverrides[o.id]
        if (override && Date.now() - override.at < STATUS_OVERRIDE_TTL_MS && o.status !== override.status) {
          return { ...o, status: override.status, current_status_since: override.since ?? o.current_status_since }
        }
        return o
      })
      .filter((o) => ['CONFIRMED', 'PREPARING', 'READY', 'IN_DELIVERY', 'COMPLETED'].includes(o.status))
      .sort((a, b) => {
        // Use current_status_since for primary sorting (oldest in status first)
        // apiTime, not new Date(): an unreadable timestamp reads as 0 instead of NaN, so this
        // comparator never silently stops ordering.
        const timeA = apiTime(a.current_status_since || a.created_at);
        const timeB = apiTime(b.current_status_since || b.created_at);
        return timeA - timeB;
      })
  }, [data, statusOverrides])

  const grouped = useMemo(() => {
    const now = new Date().getTime()
    const thirtyMinutesAgo = now - 30 * 60 * 1000

    const completedOrders = orders
      .filter(o => {
        if (o.status !== 'COMPLETED') return false
        // Use current_status_since for timing logic
        const relevantTime = o.current_status_since || o.created_at
        if (!relevantTime) return false
        return new Date(relevantTime).getTime() > thirtyMinutesAgo
      })
      .sort((a, b) => {
        // For completed, we want newest first
        // apiTime, not new Date(): an unreadable timestamp reads as 0 instead of NaN, so this
        // comparator never silently stops ordering.
        const timeA = apiTime(a.current_status_since || a.created_at)
        const timeB = apiTime(b.current_status_since || b.created_at)
        return timeB - timeA
      })

    const readyOrders = orders
      .filter(o => o.status === 'READY')
      .sort((a, b) => {
        // apiTime, not new Date(): an unreadable timestamp reads as 0 instead of NaN, so this
        // comparator never silently stops ordering.
        const timeA = apiTime(a.current_status_since || a.created_at)
        const timeB = apiTime(b.current_status_since || b.created_at)
        return timeB - timeA
      })

    // A driver's claim moves an own delivery READY → IN_DELIVERY, which used to drop
    // the card from this board entirely. Kitchen wants to keep seeing it, so these live
    // in the Handed Over lane too — pinned above the completed cards and with NO
    // 30-minute cutoff: they stay for the whole ride, and once delivered they turn
    // COMPLETED (fresh current_status_since) and get the normal 30-minute linger.
    const inDeliveryOrders = orders
      .filter(o => o.status === 'IN_DELIVERY')
      .sort((a, b) => {
        // apiTime, not new Date(): an unreadable timestamp reads as 0 instead of NaN, so this
        // comparator never silently stops ordering.
        const timeA = apiTime(a.current_status_since || a.created_at)
        const timeB = apiTime(b.current_status_since || b.created_at)
        return timeB - timeA
      })

    return {
      CONFIRMED: orders.filter(o => o.status === 'CONFIRMED'),
      PREPARING: orders.filter(o => o.status === 'PREPARING'),
      READY: readyOrders,
      COMPLETED: [...inDeliveryOrders, ...completedOrders]
    }
  }, [orders])

  // The collapse only ever hides COMPLETED cards — an in-delivery order behind a
  // "Show more" would be exactly the disappearing act this lane change is fixing.
  const hiddenCompletedCount = useMemo(() =>
    Math.max(grouped.COMPLETED.filter(o => o.status === 'COMPLETED').length - 5, 0),
  [grouped.COMPLETED])

  const displayedCompletedOrders = useMemo(() => {
    if (showAllReady) return grouped.COMPLETED
    const stillOut = grouped.COMPLETED.filter(o => o.status === 'IN_DELIVERY')
    const done = grouped.COMPLETED.filter(o => o.status === 'COMPLETED').slice(0, 5)
    return [...stillOut, ...done]
  }, [grouped.COMPLETED, showAllReady])

  const advance = async (orderId: number, next: OrderStatus) => {
    if (processingRef.current.has(orderId)) return
    processingRef.current.add(orderId)
    setProcessingOrderIds(prev => new Set(prev).add(orderId))
    try {
      const updated = await orderService.updateOrderStatus(Number(orderId), next)
      setStatusOverrides(prev => ({
        ...prev,
        [orderId]: { status: updated.status, since: updated.current_status_since ?? null, at: Date.now() }
      }))
      // Clear checkbox state for this order so it starts fresh if sent back to PREPARING
      setCompletedOrderLines(prev => {
        const newSet = new Set(prev)
        for (const key of newSet) {
          if (key.startsWith(`${orderId}-`)) newSet.delete(key)
        }
        return newSet
      })
      // Best-effort convergence only. Apollo may dedupe this onto an in-flight poll and
      // hand back a pre-mutation snapshot — the override shields the display, so the
      // result must never be interpreted as confirmation.
      await refetch()
    } catch {
      toast.error('Failed to update order status. Please try again.')
      await refetch()
    } finally {
      processingRef.current.delete(orderId)
      setProcessingOrderIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(orderId)
        return newSet
      })
    }
  }

  const toggleOrderLineComplete = (orderId: number, lineId: number) => {
    const key = `${orderId}-${lineId}`
    setCompletedOrderLines(prev => {
      const newSet = new Set(prev)
      if (newSet.has(key)) {
        newSet.delete(key)
      } else {
        newSet.add(key)
      }
      return newSet
    })
  }

  const isOrderLineComplete = (orderId: number, lineId: number) => {
    return completedOrderLines.has(`${orderId}-${lineId}`)
  }

  const toggleBundleLines = (orderId: number, lineIds: number[]) => {
    setCompletedOrderLines(prev => {
      const newSet = new Set(prev)
      const allChecked = lineIds.every(id => newSet.has(`${orderId}-${id}`))
      lineIds.forEach(id => {
        if (allChecked) {
          newSet.delete(`${orderId}-${id}`)
        } else {
          newSet.add(`${orderId}-${id}`)
        }
      })
      return newSet
    })
  }

  const isOrderReady = (order: Order) => {
    // Group lines by relationship_group_id to handle bundles
    const groups: Record<string, OrderLine[]> = {};
    const standaloneLines: OrderLine[] = [];

    order.orderLines.forEach(line => {
      if (line.relationship_group_id) {
        if (!groups[line.relationship_group_id]) groups[line.relationship_group_id] = [];
        groups[line.relationship_group_id].push(line);
      } else if (!line.parent_order_line_id) {
        standaloneLines.push(line);
      }
    });

    // Check standalone lines
    for (const line of standaloneLines) {
      const isMealDealLine = line.productVariant?.product.name.toLowerCase().includes('meal deal') || line.relationship_type === 'BUNDLE_UPGRADE';
      if (isMealDealLine) continue;
      if (!isOrderLineComplete(order.id, line.id)) return false;
    }

    // Check bundle groups
    for (const groupLines of Object.values(groups)) {
      const main = groupLines.find(l => l.relationship_type === 'MAIN_ITEM') || groupLines[0];
      const children = groupLines.filter(l => l.id !== main.id);

      if (children.length === 0) {
        // Single-item group — treat like a standalone line, must be checked
        const isMealDealLine = main.productVariant?.product.name.toLowerCase().includes('meal deal') || main.relationship_type === 'BUNDLE_UPGRADE';
        if (!isMealDealLine && !isOrderLineComplete(order.id, main.id)) return false;
      } else {
        // Real bundle — only check non-meal-deal children (skip main)
        const checkableChildren = children.filter(l =>
          !l.productVariant?.product.name.toLowerCase().includes('meal deal') && l.relationship_type !== 'BUNDLE_UPGRADE'
        );
        for (const line of checkableChildren) {
          if (!isOrderLineComplete(order.id, line.id)) return false;
        }
      }
    }

    return true;
  }

  const formatCustomizations = (line: any) => {
    if (!line.customizations || line.customizations.length === 0) return []
    
    return line.customizations.map((cust: any) => ({
      groupName: cust.group_name,
      itemName: cust.label
    }))
  }

  // Rendered via plain function call (not <Section />): a component type declared inside
  // the parent gets a new identity every render, so React would unmount and remount all
  // four lanes on each poll tick — killing taps mid-gesture and resetting lane scroll.
  const Section = ({ title, color, items, type }: { title: string, color: string, items: Order[], type: 'CONFIRMED' | 'PREPARING' | 'READY' | 'COMPLETED' }) => (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <h2 className={`text-2xl font-bold ${color}`}>{title} ({items.length})</h2>
        {type === 'COMPLETED' && hiddenCompletedCount > 0 && (
          <button
            onClick={() => setShowAllReady(!showAllReady)}
            className="text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center gap-1 bg-white px-3 py-1 rounded-full shadow-sm"
          >
            {showAllReady ? (
              <><ChevronUpIcon className="w-4 h-4" /> Show Less</>
            ) : (
              <><ChevronDownIcon className="w-4 h-4" /> Show {hiddenCompletedCount} More</>
            )}
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto space-y-4 pr-2">
        {items.map((o, index) => {
          const isOldest = index === 0 && type === 'CONFIRMED';
          const isSecondOldest = index === 1 && type === 'CONFIRMED';
          
          let priorityClass = "border-transparent";
          if (isOldest) priorityClass = "border-red-500 border-2 ring-2 ring-red-200 ring-opacity-50";
          else if (isSecondOldest) priorityClass = "border-yellow-500 border-2 ring-2 ring-yellow-200 ring-opacity-50";

          const isExpanded = type !== 'COMPLETED' || expandedCompletedOrders.has(o.id);
          const isProcessing = processingOrderIds.has(o.id)
          // Both statuses render collapsed in the Handed Over lane; only COMPLETED gets
          // the greyed-out treatment — an in-delivery card is still a live order.
          const isHandedOverCard = o.status === 'COMPLETED' || o.status === 'IN_DELIVERY'

          return (
            <div key={o.id} className={`bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 ${priorityClass} ${o.status === 'COMPLETED' ? 'bg-gray-50/50 h-fit' : ''} ${o.status === 'IN_DELIVERY' ? 'h-fit' : ''}`}>
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full font-black text-xl flex-shrink-0 ${
                      isOldest ? 'bg-red-600 text-white' : 
                      isSecondOldest ? 'bg-yellow-500 text-white' : 
                      o.status === 'COMPLETED' ? 'bg-gray-200 text-gray-400' :
                      o.status === 'IN_DELIVERY' ? 'bg-indigo-100 text-indigo-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="cursor-pointer min-w-0" onClick={() => isHandedOverCard && toggleOrderExpansion(o.id)}>
                      {/* Wraps instead of truncating: the lane is narrow and the
                          badges are flex-shrink-0, so `truncate` here clipped them
                          mid-word (the pre-order time was cut off). */}
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <div className="text-xl font-black text-gray-900 leading-none">#{o.order_number.slice(-5)}</div>
                        {o.consumingEmployee && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-600 text-white text-[11px] font-black uppercase tracking-wide whitespace-nowrap">
                            Staff Meal
                          </span>
                        )}
                        {/* Pack-for-transport signal. The order type is already spelled
                            out below in small grey caps, which is too quiet to catch at a
                            glance on a busy pass — this repeats it loudly for the one type
                            where getting it wrong means the food leaves unpackaged. */}
                        {o.order_type === 'DELIVERY' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-teal-600 text-white text-[11px] font-black uppercase tracking-wide whitespace-nowrap">
                            Delivery
                          </span>
                        )}
                        {/* Released pre-order: show the promised pickup time. Purely
                            additive — lane sorting stays on current_status_since. */}
                        {o.is_preorder && o.scheduled_for && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cyan-600 text-white text-[11px] font-black uppercase tracking-wide whitespace-nowrap">
                            Pre-order {formatTime(o.scheduled_for)}
                          </span>
                        )}
                      </div>
                      <div className="text-xs font-medium text-gray-400 mt-0.5 uppercase tracking-wider truncate">{o.order_type.replace('_', ' ')}</div>
                      {o.consumingEmployee && (
                        <div className="text-xs font-semibold text-purple-700 mt-0.5 truncate">For: {o.consumingEmployee.name}</div>
                      )}
                      {(o.customer_name || o.customer_email || o.customer_phone) && (
                        <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                          {o.customer_name && <div className="font-semibold text-gray-700 truncate">Name: {o.customer_name}</div>}
                          {o.customer_email && <div className="truncate">Email: {o.customer_email}</div>}
                          {o.customer_phone && <div>Phone: {phoneWithAccessCode(o)}</div>}
                        </div>
                      )}
                    </div>
                  </div>
                  {type !== 'COMPLETED' && (
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-bold text-gray-900">
                        {formatTime(o.current_status_since || o.created_at)}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Order Lines */}
                {isExpanded && (
                  <div className="space-y-3 mb-4 animate-in fade-in slide-in-from-top-1 duration-200">
                    {(() => {
                      // 1. Group lines by relationship_group_id
                      const groups: Record<string, OrderLine[]> = {};
                      const standaloneLines: OrderLine[] = [];

                    o.orderLines.forEach(line => {
                      if (line.relationship_group_id) {
                        if (!groups[line.relationship_group_id]) {
                          groups[line.relationship_group_id] = [];
                        }
                        groups[line.relationship_group_id].push(line);
                      } else if (!line.parent_order_line_id) {
                        // Only add as standalone if it's not a child of something else
                        // (Children will be picked up by the parent-child logic if group_id is missing)
                        standaloneLines.push(line);
                      }
                    });

                    // 2. Combine groups and standalone lines into a single list of "render blocks"
                    const renderBlocks: Array<{ main: OrderLine; children: OrderLine[]; isBundle: boolean }> = [];

                    // Process groups
                    Object.values(groups).forEach(groupLines => {
                      // Find the "main" item in the group (usually MAIN_ITEM)
                      const main = groupLines.find(l => l.relationship_type === 'MAIN_ITEM') || groupLines[0];
                      const children = groupLines.filter(l => l.id !== main.id);
                      renderBlocks.push({ main, children, isBundle: true });
                    });

                    // Process standalone lines
                    standaloneLines.forEach(line => {
                      // Check if this standalone line has children via parent_order_line_id (fallback)
                      const children = o.orderLines.filter(child => String(child.parent_order_line_id) === String(line.id));
                      renderBlocks.push({ main: line, children, isBundle: children.length > 0 });
                    });

                    // Sort blocks by the main item's id or sort_order to keep order consistent
                    renderBlocks.sort((a, b) => (a.main.sort_order || 0) - (b.main.sort_order || 0) || a.main.id - b.main.id);

                    return renderBlocks.map(block => {
                      const { main: mainLine, children, isBundle } = block;
                      
                      const mealDealChild = isBundle ? children.find(c => c.productVariant?.product.name.toLowerCase().includes('meal deal') || c.relationship_type === 'BUNDLE_UPGRADE') : undefined;
                      const filteredChildren = mealDealChild ? children.filter(c => c.id !== mealDealChild.id) : children;
                      const bundleLabel = mealDealChild?.productVariant?.product.name;
                      // All checkable line IDs in this bundle (main + non-meal-deal children)
                      const allBundleLineIds = isBundle ? [mainLine.id, ...filteredChildren.map(c => c.id)] : [];
                      const childLineIds = filteredChildren.map(c => c.id);
                      const allChildrenChecked = isBundle && childLineIds.length > 0 && childLineIds.every(id => isOrderLineComplete(o.id, id));
                      const allBundleChecked = isBundle && (allChildrenChecked || allBundleLineIds.every(id => isOrderLineComplete(o.id, id)));

                      const renderLine = (l: OrderLine, isChild: boolean, opts?: { bundleName?: string, onClickOverride?: () => void, checkedOverride?: boolean }) => {
                        const isComplete = opts?.checkedOverride ?? isOrderLineComplete(o.id, l.id)
                        const customizations = formatCustomizations(l)

                        const isMealDealLine = l.productVariant?.product.name.toLowerCase().includes('meal deal') || l.relationship_type === 'BUNDLE_UPGRADE';
                        const showCheckbox = o.status === 'PREPARING' && !isMealDealLine;

                        const isOnlyVariant = ((l.productVariant?.product as any)?.variants?.length || 0) <= 1;
                        const isDefaultName = l.productVariant?.name === 'Default';
                        const variantName = (isDefaultName && isOnlyVariant) ? '' : ` - ${l.productVariant?.name}`;

                        const handleClick = opts?.onClickOverride || (() => toggleOrderLineComplete(o.id, l.id));

                        return (
                          <div
                            key={l.id}
                            onClick={() => showCheckbox && handleClick()}
                            className={`relative p-2 rounded-lg ${isChild ? 'ml-6 bg-white/50 border border-gray-100' : 'bg-gray-50'} ${isComplete && showCheckbox ? 'bg-green-50 border border-green-200' : ''} ${showCheckbox ? 'cursor-pointer hover:bg-gray-100 active:scale-[0.98] transition-all' : ''}`}
                          >
                            {showCheckbox && isComplete && (
                              <div className="absolute top-1.5 right-1.5 w-7 h-7 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
                                <CheckIcon className="h-4 w-4 text-white stroke-[3px]" />
                              </div>
                            )}

                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline gap-2">
                                <span className="font-black text-gray-900 text-base">{l.quantity}x</span>
                                <span className="font-bold text-gray-800 text-sm">
                                  {l.productVariant?.product.name}{variantName}
                                  {opts?.bundleName && <span className="text-orange-600 font-bold"> ({opts.bundleName})</span>}
                                </span>
                              </div>

                              {customizations.length > 0 && (
                                <ul className="mt-1 space-y-0.5 pl-1">
                                  {customizations.map((custom: any, idx: number) => (
                                    <li key={idx} className="text-sm font-semibold text-gray-500 flex items-center gap-1">
                                      <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                      {custom.groupName}: {custom.itemName}
                                    </li>
                                  ))}
                                </ul>
                              )}

                              {l.notes && (
                                <div className="mt-1 text-xs text-blue-700 font-bold bg-blue-50 px-2 py-0.5 rounded-md inline-block">
                                  NOTE: {l.notes}
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      };

                      return (
                        <div key={mainLine.id} className={isBundle ? "border-2 border-orange-200 rounded-xl p-2 bg-orange-50/40 shadow-sm space-y-2" : ""}>
                          <div className="space-y-1.5">
                            {renderLine(mainLine, false, isBundle ? {
                              bundleName: bundleLabel,
                              onClickOverride: () => toggleBundleLines(o.id, allBundleLineIds),
                              checkedOverride: allBundleChecked
                            } : undefined)}
                            {filteredChildren.map(child => renderLine(child, true))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  {o.status === 'CONFIRMED' && (
                    <button
                      onClick={() => advance(o.id, 'PREPARING')}
                      disabled={isProcessing}
                      className={`flex-1 py-3 text-white text-sm font-black rounded-lg shadow-sm transition-all ${isProcessing ? 'bg-orange-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700 active:transform active:scale-95'}`}
                    >
                      {isProcessing ? 'UPDATING...' : 'START PREPARING'}
                    </button>
                  )}

                  {o.status === 'PREPARING' && (
                    <>
                      <button
                        onClick={() => advance(o.id, 'CONFIRMED')}
                        disabled={isProcessing}
                        className={`p-3 rounded-lg transition-colors ${isProcessing ? 'bg-gray-50 text-gray-300 cursor-not-allowed' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        title="Back to Confirmed"
                      >
                        <ArrowUturnLeftIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => advance(o.id, 'READY')}
                        disabled={isProcessing || !isOrderReady(o)}
                        className={`flex-1 py-3 text-white text-sm font-black rounded-lg shadow-sm transition-all ${
                          isProcessing
                            ? 'bg-green-400 cursor-not-allowed'
                            : isOrderReady(o)
                              ? 'bg-green-600 hover:bg-green-700 active:transform active:scale-95'
                              : 'bg-gray-300 cursor-not-allowed'
                        }`}
                      >
                        {isProcessing ? 'UPDATING...' : 'MARK READY'}
                      </button>
                    </>
                  )}

                  {o.status === 'READY' && (
                    <>
                      <button
                        onClick={() => advance(o.id, 'PREPARING')}
                        disabled={isProcessing}
                        className={`p-3 rounded-lg transition-colors ${isProcessing ? 'bg-gray-50 text-gray-300 cursor-not-allowed' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        title="Back to Preparing"
                      >
                        <ArrowUturnLeftIcon className="w-5 h-5" />
                      </button>
                      {/* Own deliveries leave READY only via the driver's claim on /driver
                          (claimDelivery stamps the driver; completing here would skip that
                          and terminate the order unstamped) — the API rejects it outright.
                          Platform deliveries have no driver of ours: handing to the courier
                          IS the completion. isOwnDelivery is the one shared predicate,
                          mirroring the API's guard. */}
                      {isOwnDelivery(o) ? (
                        <div className="flex-1 py-3 bg-teal-50 text-teal-700 text-sm font-black rounded-lg border-2 border-dashed border-teal-300 text-center uppercase tracking-tight">
                          Waiting for driver
                        </div>
                      ) : (
                        <button
                          onClick={() => advance(o.id, 'COMPLETED')}
                          disabled={isProcessing}
                          className={`flex-1 py-3 text-white text-sm font-black rounded-lg shadow-sm transition-all ${isProcessing ? 'bg-green-500 cursor-not-allowed' : 'bg-green-700 hover:bg-green-800 active:transform active:scale-95'}`}
                        >
                          {isProcessing ? 'UPDATING...' : 'MARK COMPLETED / GIVEN OUT'}
                        </button>
                      )}
                    </>
                  )}

                  {/* Read-only: the claiming driver owns IN_DELIVERY (deliver/undo on
                      /driver) — the kitchen just keeps eyes on it until it's delivered. */}
                  {o.status === 'IN_DELIVERY' && (
                    <div className="flex-1 py-2 px-3 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-lg border border-indigo-200 text-center uppercase tracking-tighter">
                      Out for Delivery
                    </div>
                  )}

                  {o.status === 'COMPLETED' && (
                    <div className="flex-1 py-2 px-3 bg-gray-100 text-gray-500 text-xs font-bold rounded-lg border border-gray-200 text-center uppercase tracking-tighter">
                      Completed & Handed Over
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
        {items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
            <div className="text-gray-300 mb-2">
              <CheckIcon className="w-12 h-12" />
            </div>
            <div className="text-sm font-bold text-gray-400 uppercase tracking-widest">No orders</div>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-4xl font-black text-gray-900 tracking-tight">KITCHEN DISPLAY</h1>
        <div className="flex items-center gap-4">
          <SoldOutButton />
          {loading && (
            <div className="flex items-center gap-2 text-sm font-bold text-gray-400 animate-pulse">
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              UPDATING...
            </div>
          )}
          <div className="text-sm font-bold text-gray-500 bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-8 flex-1 min-h-0">
        {Section({ title: 'Confirmed', color: 'text-blue-600', items: grouped.CONFIRMED, type: 'CONFIRMED' })}
        {Section({ title: 'Preparing', color: 'text-orange-600', items: grouped.PREPARING, type: 'PREPARING' })}
        {Section({ title: 'Ready', color: 'text-green-600', items: grouped.READY, type: 'READY' })}
        {Section({ title: 'Handed Over', color: 'text-gray-400', items: displayedCompletedOrders, type: 'COMPLETED' })}
      </div>
    </div>
  )
}

export default KitchenDisplayPage


